import React, { useEffect, useMemo, useState } from "react";
import {
	Clock,
	Briefcase,
	Eye,
	Users,
	FileText,
	ChevronLeft,
	ChevronRight,
	Download,
} from "lucide-react";

import { STORAGE_KEYS } from "./constants";
import { useCompanyDashboard } from "./CompanyDashboardProvider";
import { ActivityCard, ContentSection, FilterBar } from "./shared";

const ITEMS_PER_PAGE = 10;

const ACTIVITY_TYPE_CONFIG = {
	application: {
		icon: FileText,
		bgColor: "bg-emerald-50",
		iconColor: "text-emerald-600",
		label: "Application Received",
	},
	job_view: {
		icon: Eye,
		bgColor: "bg-blue-50",
		iconColor: "text-blue-600",
		label: "Job Viewed",
	},
	profile_view: {
		icon: Users,
		bgColor: "bg-purple-50",
		iconColor: "text-purple-600",
		label: "Profile Viewed",
	},
	job_posted: {
		icon: Briefcase,
		bgColor: "bg-[hsl(214,100%,97%)]",
		iconColor: "text-[hsl(221,83%,53%)]",
		label: "Job Posted",
	},
	job_closed: {
		icon: Clock,
		bgColor: "bg-gray-100",
		iconColor: "text-gray-600",
		label: "Job Closed",
	},
	resume_downloaded: {
		icon: Download,
		bgColor: "bg-gray-100",
		iconColor: "text-gray-600",
		label: "Resume Downloaded",
	},
};

function buildActivityItems(companyName, jobs, userName) {
	const now = new Date();
	const timeline = [];

	jobs.slice(0, 6).forEach((job, index) => {
		const dayOffset = Math.floor(index / 2);
		const baseDate = new Date(now.getTime() - (dayOffset * 24 + (index % 2) * 4 + 2) * 60 * 60 * 1000);
		const actor = userName ? { name: userName, role: "Company manager" } : null;
		const applications = Math.max(job.applicants || 0, job.status === "open" ? 1 : 0);
		const views = Math.max(job.views || 0, 8 + index * 3);

		timeline.push({
			id: `${job.id}-application`,
			type: "application",
			title: "New Application Received",
			description: `${applications} candidate${applications === 1 ? "" : "s"} applied for ${job.title}`,
			timestamp: new Date(baseDate.getTime() - 12 * 60 * 1000),
			actor,
		});

		timeline.push({
			id: `${job.id}-job-view`,
			type: "job_view",
			title: "Job Listing Viewed",
			description: `Your ${job.title} listing received ${views} views`,
			timestamp: new Date(baseDate.getTime() - 2 * 60 * 60 * 1000),
		});

		timeline.push({
			id: `${job.id}-posted`,
			type: "job_posted",
			title: "New Job Posted",
			description: `${job.title} position is now live`,
			timestamp: new Date(baseDate.getTime() - 5 * 60 * 60 * 1000),
			actor,
		});

		if (job.status === "filled") {
			timeline.push({
				id: `${job.id}-closed`,
				type: "job_closed",
				title: "Job Position Filled",
				description: `${job.title} position has been marked as filled`,
				timestamp: new Date(baseDate.getTime() - 7 * 60 * 60 * 1000),
				actor,
			});
		}

		if (applications > 0) {
			timeline.push({
				id: `${job.id}-resume`,
				type: "resume_downloaded",
				title: "Resume Downloaded",
				description: `Downloaded resume(s) for ${job.title} candidates`,
				timestamp: new Date(baseDate.getTime() - 9 * 60 * 60 * 1000),
				actor,
			});
		}
	});

	timeline.push({
		id: "profile-view",
		type: "profile_view",
		title: "Company Profile Viewed",
		description: `Candidates viewed the ${companyName} company profile`,
		timestamp: new Date(now.getTime() - 14 * 60 * 60 * 1000),
	});

	return timeline.sort((first, second) => second.timestamp.getTime() - first.timestamp.getTime());
}

function getUniqueActors(activities) {
	const actors = new Set();
	activities.forEach((activity) => {
		if (activity.actor?.name) {
			actors.add(activity.actor.name);
		}
	});
	return Array.from(actors);
}

function formatRelativeTime(date) {
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / (1000 * 60));
	const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
	const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (diffMins < 1) return "Just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays === 1) return "Yesterday";
	if (diffDays < 7) return `${diffDays}d ago`;
	return date.toLocaleDateString();
}

function groupActivitiesByDay(activities) {
	const groups = new Map();
	const now = new Date();
	const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);

	activities.forEach((activity) => {
		const activityDate = new Date(activity.timestamp);
		const activityDay = new Date(activityDate.getFullYear(), activityDate.getMonth(), activityDate.getDate());

		let dayKey;
		if (activityDay.getTime() === today.getTime()) {
			dayKey = "Today";
		} else if (activityDay.getTime() === yesterday.getTime()) {
			dayKey = "Yesterday";
		} else {
			const weekday = activityDay.toLocaleDateString("en-US", { weekday: "short" });
			const monthDay = activityDay.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
			dayKey = `${weekday} - ${monthDay}`;
		}

		if (!groups.has(dayKey)) {
			groups.set(dayKey, []);
		}
		groups.get(dayKey).push(activity);
	});

	groups.forEach((group) => {
		group.sort((first, second) => second.timestamp.getTime() - first.timestamp.getTime());
	});

	return groups;
}

function formatDateDisplay(dateString) {
	const date = new Date(dateString);
	const day = date.getDate();
	const month = date.toLocaleDateString("en-US", { month: "short" });
	const year = date.getFullYear();
	const suffix = (day === 1 || day === 21 || day === 31) ? "st"
		: (day === 2 || day === 22) ? "nd"
			: (day === 3 || day === 23) ? "rd"
				: "th";

	return `${day}${suffix} ${month}, ${year}`;
}

function getDefaultDateRange(companyId) {
	const saved = localStorage.getItem(STORAGE_KEYS.buildActivityDateRangeKey(companyId));
	if (saved) {
		try {
			return JSON.parse(saved);
		} catch {
			return null;
		}
	}

	const today = new Date();
	const monthAgo = new Date(today);
	monthAgo.setMonth(monthAgo.getMonth() - 1);

	return {
		start: monthAgo.toISOString().split("T")[0],
		end: today.toISOString().split("T")[0],
	};
}

export default function ActivityTab() {
	const { companyId, companyName, jobs, user } = useCompanyDashboard();
	const defaultRange = useMemo(() => getDefaultDateRange(companyId), [companyId]);
	const [searchQuery, setSearchQuery] = useState("");
	const [typeFilter, setTypeFilter] = useState("all");
	const [actorFilter, setActorFilter] = useState("all");
	const [startDate, setStartDate] = useState(defaultRange?.start || "");
	const [endDate, setEndDate] = useState(defaultRange?.end || "");
	const [collapsedDays, setCollapsedDays] = useState(new Set());
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		const nextRange = getDefaultDateRange(companyId);
		setStartDate(nextRange?.start || "");
		setEndDate(nextRange?.end || "");
	}, [companyId]);

	useEffect(() => {
		localStorage.setItem(
			STORAGE_KEYS.buildActivityDateRangeKey(companyId),
			JSON.stringify({ start: startDate, end: endDate }),
		);
	}, [companyId, endDate, startDate]);

	const activities = useMemo(
		() => buildActivityItems(companyName, jobs, user?.name),
		[companyName, jobs, user?.name],
	);
	const uniqueActors = useMemo(() => getUniqueActors(activities), [activities]);

	const filteredActivities = useMemo(() => {
		let filtered = activities;

		if (typeFilter !== "all") {
			filtered = filtered.filter((activity) => activity.type === typeFilter);
		}

		if (actorFilter !== "all") {
			filtered = filtered.filter((activity) => activity.actor?.name === actorFilter);
		}

		if (startDate && endDate) {
			const start = new Date(startDate);
			start.setHours(0, 0, 0, 0);
			const end = new Date(endDate);
			end.setHours(23, 59, 59, 999);
			filtered = filtered.filter((activity) => activity.timestamp >= start && activity.timestamp <= end);
		}

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter((activity) => (
				activity.title.toLowerCase().includes(query)
				|| activity.description?.toLowerCase().includes(query)
				|| activity.subtitle?.toLowerCase().includes(query)
				|| activity.actor?.name.toLowerCase().includes(query)
			));
		}

		return filtered;
	}, [activities, actorFilter, endDate, searchQuery, startDate, typeFilter]);

	useEffect(() => {
		setCurrentPage(1);
	}, [typeFilter, actorFilter, startDate, endDate, searchQuery]);

	const totalItems = filteredActivities.length;
	const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, totalItems);
	const paginatedActivities = filteredActivities.slice(startIndex, endIndex);
	const groupedActivities = useMemo(() => groupActivitiesByDay(paginatedActivities), [paginatedActivities]);
	const dayKeys = Array.from(groupedActivities.keys());

	function toggleDayCollapse(day) {
		setCollapsedDays((previous) => {
			const next = new Set(previous);
			if (next.has(day)) {
				next.delete(day);
			} else {
				next.add(day);
			}
			return next;
		});
	}

	return (
		<ContentSection companyHeader>
			<div className="mx-auto max-w-4xl space-y-4">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="text-lg font-bold text-foreground">Activity</h2>
						<p className="text-sm text-muted-foreground">
							Track recent activity on your job listings and company profile
						</p>
					</div>
				</div>

				<FilterBar
					searchValue={searchQuery}
					onSearchChange={setSearchQuery}
					searchPlaceholder="Search by job, candidate, or action..."
					dropdowns={[
						{
							value: typeFilter,
							onChange: setTypeFilter,
							options: [
								{ value: "all", label: "All Events" },
								{ value: "application", label: "Applications" },
								{ value: "job_view", label: "Job Views" },
								{ value: "profile_view", label: "Profile Views" },
								{ value: "job_posted", label: "Job Posted" },
								{ value: "job_closed", label: "Job Closed" },
								{ value: "resume_downloaded", label: "Resume Downloads" },
							],
						},
						{
							value: actorFilter,
							onChange: setActorFilter,
							options: [
								{ value: "all", label: "Actor" },
								...uniqueActors.map((actor) => ({ value: actor, label: actor })),
							],
						},
					]}
					dateRange={{
						startDate,
						endDate,
						onStartDateChange: setStartDate,
						onEndDateChange: setEndDate,
						showPicker: showDatePicker,
						onTogglePicker: () => setShowDatePicker((value) => !value),
						formatDateDisplay,
					}}
				/>

				<div className="relative mt-12">
					{dayKeys.map((day, dayIndex) => {
						const dayActivities = groupedActivities.get(day) || [];
						const isCollapsed = collapsedDays.has(day);
						const isToday = day === "Today";
						const isLastDay = dayIndex === dayKeys.length - 1;

						return (
							<div key={day} className="relative">
								{!isLastDay && (
									<div
										className={`absolute left-4 top-8 w-0.5 ${
											isToday
												? "border-l border-dashed border-muted-foreground/30"
												: "bg-border"
										}`}
										style={{ height: isCollapsed ? "24px" : "calc(100% - 8px)" }}
									/>
								)}

								<div
									className="group mb-2 flex cursor-pointer items-center justify-between"
									onClick={() => toggleDayCollapse(day)}
								>
									<div className="flex items-center gap-2">
										<div
											className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
												isToday ? "bg-primary text-white" : "bg-secondary text-muted-foreground"
											}`}
										>
											{dayActivities.length}
										</div>
										<h3 className="text-sm font-semibold text-foreground">{day}</h3>
									</div>
									<ChevronRight
										className={`h-4 w-4 text-muted-foreground transition-transform ${
											isCollapsed ? "" : "rotate-90"
										}`}
									/>
								</div>

								{!isCollapsed && (
									<div className="ml-4 space-y-2 pb-4 pl-6">
										{dayActivities.map((activity) => {
											const config = ACTIVITY_TYPE_CONFIG[activity.type];

											return (
												<ActivityCard
													key={activity.id}
													icon={config.icon}
													iconBgColor={config.bgColor}
													iconColor={config.iconColor}
													label={config.label}
													timestamp={formatRelativeTime(activity.timestamp)}
													title={activity.description || activity.title}
													actor={activity.actor}
												/>
											);
										})}
									</div>
								)}

								{isCollapsed ? <div className="h-2" /> : null}
							</div>
						);
					})}

					{dayKeys.length === 0 && (
						<div className="rounded-lg border border-border bg-white p-8 text-center">
							<div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
								<Clock className="h-5 w-5 text-muted-foreground" />
							</div>
							<h3 className="mb-1 font-semibold text-foreground">No activities found</h3>
							<p className="text-sm text-muted-foreground">
								{searchQuery || typeFilter !== "all" || actorFilter !== "all" || (startDate && endDate)
									? "Try adjusting your filters to see more results."
									: "Activities will appear here as they happen."}
							</p>
						</div>
					)}
				</div>

				{totalItems > 0 && (
					<div className="flex items-center justify-between border-t border-border pt-4">
						<span className="text-sm text-muted-foreground">
							Showing {startIndex + 1} - {endIndex} of {totalItems}
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								onClick={() => setCurrentPage((value) => Math.max(1, value - 1))}
								disabled={currentPage === 1}
								className="rounded-lg border border-border bg-white p-1.5 transition-colors hover:bg-secondary/50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<ChevronLeft className="h-4 w-4 text-muted-foreground" />
							</button>
							<span className="px-2 text-sm text-muted-foreground">
								Page {currentPage} of {totalPages}
							</span>
							<button
								type="button"
								onClick={() => setCurrentPage((value) => Math.min(totalPages, value + 1))}
								disabled={currentPage === totalPages}
								className="rounded-lg border border-border bg-white p-1.5 transition-colors hover:bg-secondary/50 disabled:cursor-not-allowed disabled:opacity-50"
							>
								<ChevronRight className="h-4 w-4 text-muted-foreground" />
							</button>
						</div>
					</div>
				)}
			</div>
		</ContentSection>
	);
}
